import {
	createContext,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from 'react';

import { useSelectedStore } from 'contexts';
import { useRequiredContext } from 'hooks';
import { useRelatedProducts } from 'hooks/data-fetching';
import type { RelationType } from 'models/api';
import type { VariantId } from 'models/product';
import type { RelatedProductCard } from 'models/productCard';
import { empty } from 'utils/helpers';

interface RelatedProductsContextType {
	hasNextPage: boolean;
	isLoading: boolean;
	isLoadingMore: boolean;
	isOpen: boolean;
	items: RelatedProductCard[];
	loadMore: () => void;
	toggleOpen: () => void;
}

function useRelatedProductsContextValue({
	hasRelationType,
	isActiveWhenClosed = false,
	isInitialOpen = false,
	pageSize,
	relationType,
	variantId,
}: {
	hasRelationType: boolean;
	isActiveWhenClosed?: boolean;
	isInitialOpen?: boolean;
	pageSize?: number;
	relationType: RelationType;
	variantId: VariantId;
}) {
	const { selectedStore } = useSelectedStore();
	const [isOpen, setIsOpen] = useState(isInitialOpen);
	const toggleOpen = useCallback(() => setIsOpen((prev) => !prev), []);
	const { hasNextPage, isLoading, isLoadingMore, items, loadMore } =
		useRelatedProducts({
			variantId,
			relationType,
			pageSize,
			isActive: hasRelationType && (isOpen || isActiveWhenClosed),
			returnStockData: true,
			storeId: selectedStore?.id,
		});

	return useMemo(
		() => ({
			hasNextPage,
			isLoading,
			isLoadingMore: Boolean(isLoadingMore),
			items: items ?? empty.array,
			loadMore,
			isOpen,
			toggleOpen,
		}),
		[
			hasNextPage,
			isLoading,
			isLoadingMore,
			isOpen,
			items,
			loadMore,
			toggleOpen,
		],
	);
}

export const AccessoriesContext = createContext<
	RelatedProductsContextType | undefined
>(undefined);

interface AccessoriesContextProviderProps {
	children: ReactNode;
	hasAccessories: boolean;
	variantId: VariantId;
}
export function AccessoriesContextProvider({
	children,
	hasAccessories,
	variantId,
}: AccessoriesContextProviderProps) {
	const value = useRelatedProductsContextValue({
		hasRelationType: hasAccessories,
		isActiveWhenClosed: true,
		relationType: 'Accessories',
		variantId,
	});
	return <AccessoriesContext value={value}>{children}</AccessoriesContext>;
}
AccessoriesContextProvider.displayName = 'AccessoriesContextProvider';

export function useAccessoriesContext() {
	return useRequiredContext(AccessoriesContext, 'AccessoriesContext');
}

export const BelongsToBundleContext = createContext<
	RelatedProductsContextType | undefined
>(undefined);

interface BelongsToBundleContextProviderProps {
	children: ReactNode;
	hasBelongsToBundle: boolean;
	variantId: VariantId;
}
export function BelongsToBundleContextProvider({
	children,
	hasBelongsToBundle,
	variantId,
}: BelongsToBundleContextProviderProps) {
	const value = useRelatedProductsContextValue({
		hasRelationType: hasBelongsToBundle,
		relationType: 'BelongsToBundle',
		variantId,
		isInitialOpen: true,
	});
	return (
		<BelongsToBundleContext value={value}>{children}</BelongsToBundleContext>
	);
}
BelongsToBundleContextProvider.displayName = 'BelongsToBundleContextProvider';

export function useBelongsToBundleContext() {
	return useRequiredContext(BelongsToBundleContext, 'BelongsToBundleContext');
}

export const BelongsToContext = createContext<
	RelatedProductsContextType | undefined
>(undefined);

interface BelongsToContextProviderProps {
	children: ReactNode;
	hasBelongsTo: boolean;
	isInitialOpen: boolean;
	variantId: VariantId;
}
export function BelongsToContextProvider({
	children,
	hasBelongsTo,
	isInitialOpen,
	variantId,
}: BelongsToContextProviderProps) {
	const value = useRelatedProductsContextValue({
		hasRelationType: hasBelongsTo,
		relationType: 'BelongsTo',
		variantId,
		isInitialOpen,
	});
	return <BelongsToContext value={value}>{children}</BelongsToContext>;
}
BelongsToContextProvider.displayName = 'BelongsToContextProvider';

export function useBelongsToContext() {
	return useRequiredContext(BelongsToContext, 'BelongsToContext');
}

export const SparePartsContext = createContext<
	RelatedProductsContextType | undefined
>(undefined);

interface SparePartsContextProviderProps {
	children: ReactNode;
	hasSpareParts: boolean;
	variantId: VariantId;
}
export function SparePartsContextProvider({
	children,
	hasSpareParts,
	variantId,
}: SparePartsContextProviderProps) {
	const value = useRelatedProductsContextValue({
		hasRelationType: hasSpareParts,
		relationType: 'SpareParts',
		variantId,
	});
	return <SparePartsContext value={value}>{children}</SparePartsContext>;
}
SparePartsContextProvider.displayName = 'SparePartsContextProvider';

export function useSparePartsContext() {
	return useRequiredContext(SparePartsContext, 'SparePartsContext');
}

export const BundleContext = createContext<
	RelatedProductsContextType | undefined
>(undefined);

interface BundleContextProviderProps {
	children: ReactNode;
	hasBundle: boolean;
	variantId: VariantId;
}
export function BundleContextProvider({
	children,
	hasBundle,
	variantId,
}: BundleContextProviderProps) {
	const value = useRelatedProductsContextValue({
		hasRelationType: hasBundle,
		isInitialOpen: true,
		// Increase page size to ensure all products are returned, hopefully.
		pageSize: 30,
		relationType: 'Bundle',
		variantId,
	});
	return <BundleContext value={value}>{children}</BundleContext>;
}
BundleContextProvider.displayName = 'BundleContextProvider';

export function useBundleContext() {
	return useRequiredContext(BundleContext, 'BundleContext');
}
